import { logError } from 'lib/logging'

/** The index returns at most this many rows a page whatever limit is asked for. */
export const COHORT_PAGE = 1000

/**
 * A ceiling on how many pages one read will turn, so a practice far deeper
 * than any seen so far degrades to a partial answer and a logged fault rather
 * than an open-ended run of requests. Hamblin, the deepest measured, holds
 * 2,560 future visits and 2,684 people with unbooked treatment: three pages
 * of each.
 */
export const COHORT_MAX_PAGES = 20

/**
 * A whole list off the index, a page at a time, for the boards that have to
 * hold a cohort locally: a split the index cannot make, or a filter answered
 * by a different index than the one being listed. `page` returns undefined
 * when the read has been superseded, which ends it with what it has.
 */
export async function readAllPages<T>(
  label: string,
  page: (index: number) => Promise<{ hits: T[]; total: number } | undefined>,
): Promise<T[]> {
  const rows: T[] = []
  for (let index = 0; index < COHORT_MAX_PAGES; index++) {
    const result = await page(index)
    if (!result) return rows
    rows.push(...result.hits)
    if (result.hits.length < COHORT_PAGE || rows.length >= result.total) return rows
    if (index === COHORT_MAX_PAGES - 1) {
      logError(`Action Center: ${label} read stopped short`, new Error(`${result.total} rows, read ${rows.length}`))
    }
  }
  return rows
}

/** What a local cohort read hands the band: done or not, and who survived. */
export type CohortRead<T> = {
  /** True once the read has finished, whether or not it found anyone. */
  ready: boolean
  contacts: T[]
  error?: string
}
